import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiWifiOff, FiRefreshCw, FiWifi, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import useNetworkStatus from '../hooks/useNetworkStatus';
import '../styles/OfflinePage.css';

const OfflinePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isOnline, 
    isConnectionSuitable, 
    connection,
    queueAction 
  } = useNetworkStatus();
  
  const [lastPath, setLastPath] = useState('/');
  const [isChecking, setIsChecking] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [queuedActions, setQueuedActions] = useState([]);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Get the previous location from navigation state
  useEffect(() => {
    const fromPath = location.state?.from?.pathname || '/';
    setLastPath(fromPath);
  }, [location]);

  // Check if we've come back online
  useEffect(() => {
    if (isOnline) {
      const timer = setTimeout(() => {
        navigate(lastPath, { replace: true });
      }, 1000); // Short delay to ensure connection is stable
      
      return () => clearTimeout(timer);
    }
  }, [isOnline, navigate, lastPath]);

  const handleRetry = async () => {
    if (isOnline) {
      navigate(lastPath, { replace: true });
      return;
    }

    setIsChecking(true);
    setCheckCount(prev => prev + 1);
    
    try {
      // Try to make a lightweight request to check connectivity
      const response = await fetch(window.location.origin, { 
        method: 'HEAD',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (response.ok) {
        navigate(lastPath, { replace: true });
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      logger.error('Still offline:', error); // Update console log to use logger utility
      // If still offline, show error after animation completes
      setTimeout(() => {
        setIsChecking(false);
      }, 1000);
    }
  };

  const queueCurrentAction = () => {
    const action = {
      id: `action-${Date.now()}`,
      type: 'navigate',
      path: lastPath,
      timestamp: new Date().toISOString()
    };
    
    queueAction(() => {
      logger.log(`Executing queued action: ${action.id}`);
      navigate(action.path, { replace: true });
    }, action.id);
    
    setQueuedActions(prev => [...prev, action]);
  };

  const getConnectionStatus = () => {
    if (isOnline) {
      const isSuitable = isConnectionSuitable({ minSpeed: 0.5, maxLatency: 1000 });
      return {
        icon: isSuitable ? <FiWifi className="online" /> : <FiAlertTriangle className="warning" />,
        text: isSuitable ? 'Back Online' : 'Poor Connection',
        description: isSuitable 
          ? 'Redirecting you back...' 
          : 'Your connection is too slow for this content.'
      };
    }
    
    return {
      icon: <FiWifiOff className="offline" />,
      text: 'You\'re Offline',
      description: 'We can\'t connect to the internet right now.'
    };
  };

  const status = getConnectionStatus();

  return (
    <div className="offline-container">
      <div className="offline-content">
        <div className="status-icon">
          {status.icon}
        </div>
        
        <h1>{status.text}</h1>
        <p className="status-description">{status.description}</p>
        
        {!isOnline && (
          <>
            <button 
              className="retry-button" 
              onClick={handleRetry}
              disabled={isChecking}
            >
              {isChecking ? (
                <span className="button-content">
                  <FiRefreshCw className="spin" /> Checking...
                </span>
              ) : (
                <span className="button-content">
                  <FiRefreshCw /> Retry Connection
                </span>
              )}
            </button>
            
            <button 
              className="queue-button"
              onClick={queueCurrentAction}
              disabled={queuedActions.some(a => a.path === lastPath)}
            >
              <FiInfo /> {queuedActions.some(a => a.path === lastPath) ? 'Action Queued' : 'Queue for When Online'}
            </button>
            
            {checkCount > 1 && (
              <div className="troubleshoot-tip">
                <h3>Having trouble connecting?</h3>
                <ol>
                  <li>Check your Wi-Fi or mobile data connection</li>
                  <li>Turn on Airplane mode, then turn it off</li>
                  <li>Restart your device</li>
                  <li>Check for any network outages in your area</li>
                </ol>
              </div>
            )}
            
            <button 
              className="debug-toggle"
              onClick={() => setShowDebugInfo(!showDebugInfo)}
            >
              {showDebugInfo ? 'Hide' : 'Show'} Debug Info
            </button>
            
            {showDebugInfo && (
              <div className="debug-info">
                <h4>Connection Details</h4>
                <pre>{JSON.stringify({
                  lastPath,
                  isOnline,
                  checkCount,
                  queuedActions: queuedActions.length,
                  connection: {
                    type: connection.type,
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt,
                    saveData: connection.saveData
                  }
                }, null, 2)}</pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OfflinePage;
